"use client"

import type { TikzShapeId } from "@/lib/ribbon/tikz-shapes"

const R = "/ribbion_menu"

/** Native viewBox of `shapesHover.svg` — used to place clickable hotspots. */
const HOVER_W = 156
const HOVER_H = 96

/**
 * Hit targets matching the shape tiles embedded in `shapesHover.svg`
 * (24×24 cells in two rows under the search field).
 */
const SHAPE_HOTSPOTS: {
  id: TikzShapeId
  label: string
  /** Matching asset in public/ribbion_menu (for aria / future preview). */
  icon: string
  x: number
  y: number
  w: number
  h: number
}[] = [
  { id: "circle", label: "Circle", icon: "circleIcon.svg", x: 6, y: 36, w: 24, h: 24 },
  { id: "rectangle", label: "Rectangle", icon: "rectangleIcon.svg", x: 36, y: 36, w: 24, h: 24 },
  { id: "line", label: "Line", icon: "lineIcon.svg", x: 66, y: 36, w: 24, h: 24 },
  { id: "ellipse", label: "Ellipse", icon: "ellipseIcon.svg", x: 96, y: 36, w: 24, h: 24 },
  { id: "arc", label: "Arc", icon: "arcIcon.svg", x: 126, y: 36, w: 24, h: 24 },
  { id: "bezier", label: "Bezier", icon: "bezireIcon.svg", x: 6, y: 66, w: 24, h: 24 },
  { id: "diamond", label: "Diamond", icon: "dimondIcon.svg", x: 36, y: 66, w: 24, h: 24 },
  { id: "trapezium", label: "Trapezium", icon: "trapeziumIcon.svg", x: 66, y: 66, w: 24, h: 24 },
  { id: "semicircle", label: "Semicircle", icon: "semicircleIcon.svg", x: 96, y: 66, w: 24, h: 24 },
  { id: "cylinder", label: "Cylinder", icon: "cylinderIcon.svg", x: 126, y: 66, w: 24, h: 24 },
]

type ShapesPickerProps = {
  onSelect: (id: TikzShapeId) => void
}

/** Display scale for `shapesHover.svg` (native 156×96). */
const DISPLAY_SCALE = 1.35

/**
 * Clickable `shapesHover.svg` palette — no custom card UI.
 * Hotspots sit over the built-in shape tiles; click inserts that TikZ shape.
 */
export function ShapesPicker({ onSelect }: ShapesPickerProps) {
  return (
    <div
      className="relative overflow-hidden rounded-lg shadow-lg"
      style={{ width: HOVER_W * DISPLAY_SCALE, height: HOVER_H * DISPLAY_SCALE }}
    >
      <img
        src={`${R}/shapesHover.svg`}
        alt="Shapes"
        draggable={false}
        className="pointer-events-none absolute inset-0 h-full w-full select-none"
      />

      {SHAPE_HOTSPOTS.map((spot) => (
        <button
          key={spot.id}
          type="button"
          aria-label={spot.label}
          title={spot.label}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onSelect(spot.id)}
          className="absolute rounded-[4px] hover:bg-white/20 focus-visible:bg-white/25 focus-visible:outline-none"
          style={{
            left: `${(spot.x / HOVER_W) * 100}%`,
            top: `${(spot.y / HOVER_H) * 100}%`,
            width: `${(spot.w / HOVER_W) * 100}%`,
            height: `${(spot.h / HOVER_H) * 100}%`,
          }}
        />
      ))}
    </div>
  )
}
